import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import { SERVER_BASE_URL } from "../Config";

type OrderSummary = {
  id: number;
  patientName: string;
  drugName: string;
  orderDate: string;
  status: string;
};

const ContactSupport: React.FC = () => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const [orderSearch, setOrderSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const token = localStorage.getItem("providerToken");
        const response = await fetch(`${SERVER_BASE_URL}/provider_orders`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const data = await response.json();
        const summaries = data.orders.map((order: any) => ({
          id: order.id,
          patientName: order.patient?.name || "Unknown",
          drugName: order.products?.[0]?.product?.name || "Unknown",
          status: order.status || "",
          orderDate: order.created
            ? new Date(order.created).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : "",
        }));
        setOrders(summaries);
      } catch (err) {
        console.error("Failed to load orders:", err);
      }
    };
    loadOrders();
  }, []);

  const filteredOrders = useMemo(() => {
    if (!orderSearch.trim()) return orders;
    const q = orderSearch.toLowerCase();
    return orders.filter(
      (o) =>
        o.patientName.toLowerCase().includes(q) ||
        o.drugName.toLowerCase().includes(q) ||
        o.id.toString().includes(q)
    );
  }, [orders, orderSearch]);

  const handleToggleOrder = (orderId: number) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSubmit = async () => {
    if (!subject.trim() || !message.trim()) {
      setError("Please fill in both subject and message.");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const token = localStorage.getItem("providerToken");
      const response = await fetch(`${SERVER_BASE_URL}/contact-support`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          subject,
          message,
          orderIds: selectedOrderIds,
        }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccess(data.message);
        setSubject("");
        setMessage("");
        setSelectedOrderIds([]);
      } else {
        setError(data.message || "Failed to send. Please try again.");
      }
    } catch (err) {
      setError("Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Contact Support
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Send a message to the pharmacy. You can optionally attach orders for
        reference.
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        label="Subject"
        fullWidth
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        sx={{ mb: 2 }}
      />

      <TextField
        label="Message"
        fullWidth
        multiline
        rows={6}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        sx={{ mb: 3 }}
      />

      {orders.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Attach Orders (optional)
          </Typography>
          {selectedOrderIds.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              {selectedOrderIds.length} order{selectedOrderIds.length !== 1 ? "s" : ""} selected
            </Typography>
          )}
          <TextField
            size="small"
            placeholder="Search orders..."
            fullWidth
            value={orderSearch}
            onChange={(e) => setOrderSearch(e.target.value)}
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Paper variant="outlined" sx={{ maxHeight: 250, overflow: "auto" }}>
            <List dense>
              {filteredOrders.map((order) => (
                <ListItem key={order.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleToggleOrder(order.id)}
                    dense
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        checked={selectedOrderIds.includes(order.id)}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={`#${order.id} — ${order.patientName} — ${order.drugName}`}
                      secondary={order.orderDate}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              {filteredOrders.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No orders found"
                    sx={{ textAlign: "center", color: "text.secondary" }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Box>
      )}

      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={handleSubmit}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
      >
        {loading ? "Sending..." : "Send Message"}
      </Button>
    </Container>
  );
};

export default ContactSupport;
